"use client";

/**
 * Marketing homepage — concrete-use-case pass (2026-10), on top of the V5
 * context×governance copy (2026-07-18).
 *
 * Order rule: a first-time visitor must understand the JOB before the
 * architecture — concrete outcome → how it works → why it is safe → broader
 * platform → governance positioning. Real-world feedback showed visitors
 * understood "connects support desks and documentation" but not that
 * FixControl follows a customer report into the code and back out as a
 * tested change; the old hero ("AI kan veel.") and the layer stack in
 * fourth position made them work for it.
 *
 *   1. Hero               — "From issue to tested fix." + a subheadline naming
 *                           both starting points (customer report, engineering
 *                           issue) + the by-default approval line.
 *   2. How it works       — two starting points (support / engineering)
 *                           converging into the eight-step chain in three
 *                           phases (workflow-section.tsx) + the principle.
 *   3. Where it fits      — support AI / coding assistant / acting on its own
 *                           (fit-section.tsx).
 *   4. Example            — "invoice export fails" in seven moments, a short
 *                           engineering-originated example (retry logic sends
 *                           webhooks twice) + logo strip below.
 *   5. App shot           — one real product screenshot.
 *   6. Control            — rules + the customer capability ladder.
 *   7. Value band         — business-value calculator link.
 *   8. Video              — the one-minute product video (poster + modal,
 *                           never autoplay) + the two demo cards.
 *   9. Use cases          — the same rules beyond bug fixes; tiles driven by
 *                           the use-case registry's availability.
 *  10. Sources / Project  — what context FixControl uses, and the project as
 *                           its boundary.
 *  11. Layers             — the vertical platform layer visual + the
 *                           seven-step plain-language translation.
 *  12. Boundaries         — "replaces nothing" triptych + /compare links.
 *  13. Trust              — verify-us-yourself + what does not exist yet.
 *  14. Final CTA + footer.
 *
 * All copy lives in the `landing.*` namespace (NL canonical, EN lockstep) and
 * is guarded by check:marketing-jargon + check:marketing-abstractions.
 */

import Link from "next/link";
import Image from "next/image";
import {
  ArrowRight,
  ArrowUp,
  CheckCircle2,
  Eye,
  FileText,
  GitBranch,
  Mail,
  MessageSquare,
  Rocket,
  ScrollText,
  ShieldCheck,
  Ticket,
  Users,
} from "lucide-react";
import { useState } from "react";
import { BrandLogo } from "@/components/brand";
import { LandingNav } from "@/components/landing-nav";
import { FitSection } from "@/components/landing/fit-section";
import { WorkflowSection } from "@/components/landing/workflow-section";
import { VideoCard } from "@/components/marketing/video/video-card";
import { VideoModal } from "@/components/marketing/video/video-modal";
import { VideoPoster } from "@/components/marketing/video/video-poster";
import { VideoTranscript } from "@/components/marketing/video/video-transcript";
import { getMarketingVideo } from "@/lib/marketing-videos";
import { Reveal } from "@/components/reveal";
import { SmoothScroll } from "@/components/smooth-scroll";
import { useT } from "@/i18n";
import { PUBLIC_EMAIL } from "@/lib/contact";
import { COMPANY } from "@/lib/company";
import { resolveUseCase } from "@/lib/use-cases/registry";
import type { ProjectCapabilityKind } from "@/lib/use-cases/capabilities";

export function LandingPage() {
  return (
    <div className="min-h-screen bg-bg-base text-fg">
      <SmoothScroll />
      <LandingNav />
      <main>
        <Hero />
        <WorkflowSection />
        <FitSection />
        <Example />
        <AppShotBand />
        <Control />
        <ValueBand />
        <VideoSection />
        <UseCasesSection />
        <Sources />
        <ProjectBoundary />
        <Layers />
        <Boundaries />
        <Trust />
        <FinalCTA />
      </main>
      <Footer />
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/*  HERO                                                                    */
/* -------------------------------------------------------------------------- */

function Hero() {
  const t = useT();
  return (
    <section id="product" className="relative overflow-hidden border-b border-line-subtle">
      <div
        aria-hidden
        className="pointer-events-none absolute inset-x-0 -top-40 h-[680px] bg-[radial-gradient(ellipse_at_top,rgba(244,63,142,0.18),transparent_60%)]"
      />
      <div className="relative mx-auto flex max-w-5xl flex-col items-center px-5 pt-20 pb-14 text-center sm:px-6 md:pt-28 md:pb-20">
        {/* The concrete scenario a support or dev lead recognises themselves
            in — the question that started this page — above the short H1. */}
        <Reveal>
          <p className="mb-6 max-w-2xl text-balance text-[14px] font-medium leading-snug text-brand-300 md:text-[15px]">
            {t("landing.hero.eyebrow")}
          </p>
        </Reveal>
        <Reveal>
          <h1 className="max-w-4xl text-balance text-[42px] font-semibold leading-[1.06] tracking-tight text-fg-bright sm:text-6xl md:text-7xl">
            {t("landing.hero.title")}
          </h1>
        </Reveal>

        <Reveal delay={80}>
          <p className="mt-7 max-w-2xl text-pretty text-lg leading-relaxed text-fg-dim md:text-xl">
            {t("landing.hero.sub")}
          </p>
        </Reveal>

        <Reveal delay={140}>
          <p className="mt-4 max-w-2xl text-pretty text-[15px] leading-relaxed text-fg-dim">
            {t("landing.hero.second")}
          </p>
        </Reveal>

        {/* The primary CTA scrolls to the eight-step chain directly below —
            understanding the product never depends on booking a demo. */}
        <Reveal delay={220}>
          <div className="mt-10 flex flex-wrap items-center justify-center gap-3">
            <PrimaryCTA href="#how">
              {t("landing.hero.ctaPrimary")}
              <ArrowRight size={16} />
            </PrimaryCTA>
            <Link
              href="/signup"
              className="inline-flex items-center gap-1.5 rounded-lg border border-line bg-bg-raised px-4 py-2.5 text-[14px] text-fg hover:bg-bg-hover hover:border-line-strong transition"
            >
              {t("landing.hero.ctaSecondary")}
            </Link>
          </div>
        </Reveal>

        <Reveal delay={280}>
          <p className="mt-6 max-w-2xl text-balance text-[14px] text-fg-dim">{t("landing.hero.underCtas")}</p>
        </Reveal>
      </div>
    </section>
  );
}

/* -------------------------------------------------------------------------- */
/*  VALUE BAND — business-value calculator, after the "why it is safe" block   */
/* -------------------------------------------------------------------------- */

function ValueBand() {
  const t = useT();
  return (
    <section className="border-b border-line-subtle bg-bg-raised/40">
      <div className="mx-auto flex max-w-6xl flex-col items-center gap-5 px-5 py-12 text-center sm:px-6 md:flex-row md:justify-between md:text-left">
        <Reveal>
          <div className="max-w-2xl">
            <p className="text-balance text-[18px] font-semibold leading-snug tracking-tight text-fg-bright md:text-[20px]">
              {t("landing.valueBand.title")}
            </p>
            <p className="mt-1.5 text-[13.5px] leading-relaxed text-fg-dim">
              {t("landing.valueBand.body")}
            </p>
          </div>
        </Reveal>
        <Reveal delay={80}>
          <div className="flex flex-wrap items-center justify-center gap-3">
            <Link
              href="/business-value#calculator"
              className="inline-flex items-center gap-1.5 rounded-lg bg-brand-grad px-4 py-2.5 text-[14px] font-medium text-white shadow-glow transition hover:brightness-110"
            >
              {t("landing.valueBand.ctaCalc")}
              <ArrowRight size={15} />
            </Link>
            <Link
              href="#video"
              className="inline-flex items-center gap-1.5 rounded-lg border border-line bg-bg-raised px-4 py-2.5 text-[14px] text-fg hover:bg-bg-hover hover:border-line-strong transition"
            >
              {t("landing.valueBand.ctaDemo")}
            </Link>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

/* -------------------------------------------------------------------------- */
/*  SOURCES — what FixControl reads: source kinds, no logos                    */
/* -------------------------------------------------------------------------- */

const SOURCE_ITEMS = [
  { key: "tickets", Icon: Ticket },
  { key: "code", Icon: GitBranch },
  { key: "conversations", Icon: MessageSquare },
  { key: "mail", Icon: Mail },
  { key: "docs", Icon: FileText },
  { key: "deployments", Icon: Rocket },
  { key: "decisions", Icon: ScrollText },
] as const;

function Sources() {
  const t = useT();
  return (
    <section id="sources" className="border-b border-line-subtle">
      <div className="mx-auto max-w-6xl px-5 py-20 sm:px-6 md:py-28">
        <Reveal>
          <SectionHeader
            title={t("landing.sources.title")}
            description={t("landing.sources.intro")}
          />
        </Reveal>
        <div className="mt-10 grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4">
          {SOURCE_ITEMS.map(({ key, Icon }, i) => (
            <Reveal key={key} delay={i * 50}>
              <div className="card h-full p-4">
                <Icon size={16} className="text-brand-300" />
                <div className="mt-3 text-[14px] font-medium text-fg-bright">
                  {t(`landing.sources.items.${key}.title`)}
                </div>
                <p className="mt-1.5 text-[12.5px] leading-relaxed text-fg-dim">
                  {t(`landing.sources.items.${key}.body`)}
                </p>
              </div>
            </Reveal>
          ))}
        </div>
        <Reveal delay={120}>
          <p className="mt-8 max-w-3xl text-[15px] font-medium text-fg-bright">
            {t("landing.sources.outro")}
          </p>
        </Reveal>
      </div>
    </section>
  );
}

/* -------------------------------------------------------------------------- */
/*  PROJECT AS CONTEXT BOUNDARY                                             */
/* -------------------------------------------------------------------------- */

function ProjectBoundary() {
  const t = useT();
  return (
    <section id="projects" className="border-b border-line-subtle">
      <div className="mx-auto max-w-6xl px-5 py-20 sm:px-6 md:py-28">
        <Reveal>
          <SectionHeader
            title={t("landing.project.title")}
            description={t("landing.project.intro")}
          />
        </Reveal>
        <div className="mt-10 grid gap-4 md:grid-cols-2">
          <Reveal>
            <div className="card h-full p-6">
              <div className="text-[13px] font-mono uppercase tracking-[0.14em] text-brand-300">
                {t("landing.project.exampleATitle")}
              </div>
              <p className="mt-3 text-[14px] leading-relaxed text-fg-dim">
                {t("landing.project.exampleABody")}
              </p>
            </div>
          </Reveal>
          <Reveal delay={80}>
            <div className="card h-full p-6">
              <div className="text-[13px] font-mono uppercase tracking-[0.14em] text-brand-300">
                {t("landing.project.exampleBTitle")}
              </div>
              <p className="mt-3 text-[14px] leading-relaxed text-fg-dim">
                {t("landing.project.exampleBBody")}
              </p>
            </div>
          </Reveal>
        </div>
        <Reveal delay={140}>
          <p className="mt-8 max-w-3xl text-[15px] font-medium text-fg-bright">
            {t("landing.project.outro")}
          </p>
        </Reveal>
      </div>
    </section>
  );
}

/* -------------------------------------------------------------------------- */
/*  LAYERS — the platform view, after the concrete chain has been understood   */
/* -------------------------------------------------------------------------- */

/** Bottom → top. Rendered top-down starting with people, so "your systems"
 *  form the foundation at the bottom. This is the platform picture; the
 *  concrete step-by-step chain lives in workflow-section.tsx near the top. */
const LAYERS = ["l6", "l5", "l4", "l3", "l2", "l1"] as const;
const STEPS = ["s1", "s2", "s3", "s4", "s5", "s6", "s7"] as const;

function Layers() {
  const t = useT();
  return (
    <section id="platform" className="border-b border-line-subtle">
      <div className="mx-auto max-w-6xl px-5 py-20 sm:px-6 md:py-28">
        <Reveal>
          <SectionHeader title={t("landing.layers.title")} description={t("landing.layers.note")} />
        </Reveal>
        <div className="mt-12 grid gap-10 md:grid-cols-2 md:gap-14">
          {/* The vertical layer diagram. The layer visual must never appear
              without the plain-language steps next to it (V5.3). */}
          <div className="flex flex-col gap-2" aria-label={t("landing.layers.title")}>
            {LAYERS.map((key, i) => (
              <Reveal key={key} delay={i * 60}>
                <div
                  className={`card flex items-center gap-3 px-4 py-3.5 ${
                    key === "l3" || key === "l4" ? "border-brand-500/30 bg-brand-500/5" : ""
                  }`}
                >
                  {key === "l6" ? (
                    <Users size={15} className="shrink-0 text-brand-300" />
                  ) : key === "l1" ? (
                    <Ticket size={15} className="shrink-0 text-fg-muted" />
                  ) : (
                    <ArrowUp size={15} className="shrink-0 text-fg-muted" />
                  )}
                  <span className="text-[13.5px] leading-snug text-fg">
                    {t(`landing.layers.${key}`)}
                  </span>
                </div>
              </Reveal>
            ))}
          </div>
          <div>
            <Reveal>
              <h3 className="text-xl font-semibold tracking-tight text-fg-bright">
                {t("landing.layers.stepsTitle")}
              </h3>
            </Reveal>
            <ol className="mt-5 space-y-4">
              {STEPS.map((key, i) => (
                <Reveal as="li" key={key} delay={i * 50} className="flex gap-3">
                  <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full border border-brand-500/30 bg-brand-500/10 text-[12px] font-semibold text-brand-300">
                    {i + 1}
                  </span>
                  <span className="text-[14px] leading-relaxed text-fg-dim">
                    {t(`landing.layers.${key}`)}
                  </span>
                </Reveal>
              ))}
            </ol>
          </div>
        </div>
      </div>
    </section>
  );
}

/* -------------------------------------------------------------------------- */
/*  THE EXAMPLE IN SEVEN MOMENTS + LOGO STRIP (mid-page, never above fold)  */
/* -------------------------------------------------------------------------- */

const MOMENTS = ["m1", "m2", "m3", "m4", "m5", "m6", "m7"] as const;
const ENGINEERING_EXAMPLE_STEPS = ["s1", "s2", "s3", "s4", "s5"] as const;

/* -------------------------------------------------------------------------- */
/*  APP SHOT — one real product screenshot right after the example flow    */
/* -------------------------------------------------------------------------- */

function AppShotBand() {
  const t = useT();
  return (
    <section className="border-b border-line-subtle bg-bg-raised/40">
      <div className="mx-auto max-w-6xl px-5 py-20 sm:px-6 md:py-24">
        <Reveal>
          <div className="mx-auto max-w-2xl text-center">
            <h2 className="text-balance text-2xl font-semibold tracking-tight text-fg-bright md:text-3xl">
              {t("landing.appShot.title")}
            </h2>
            <p className="mt-3 text-[14px] leading-relaxed text-fg-dim">
              {t("landing.appShot.body")}
            </p>
          </div>
        </Reveal>
        <Reveal delay={100}>
          <figure className="mx-auto mt-10 max-w-4xl overflow-hidden rounded-2xl border border-line-subtle bg-bg-raised shadow-xl">
            <div className="flex items-center gap-2 border-b border-line-subtle px-4 py-2.5">
              <span aria-hidden className="flex gap-1.5">
                <span className="h-2.5 w-2.5 rounded-full bg-line-subtle" />
                <span className="h-2.5 w-2.5 rounded-full bg-line-subtle" />
                <span className="h-2.5 w-2.5 rounded-full bg-line-subtle" />
              </span>
              <span className="ml-1 text-[11px] font-mono uppercase tracking-[0.18em] text-fg-muted">
                {t("landing.appShot.caption")}
              </span>
            </div>
            <div className="relative aspect-[16/10] bg-bg-base">
              <Image
                src="/screenshots/home.png"
                alt={t("landing.appShot.alt")}
                fill
                className="object-cover object-top"
                sizes="(max-width: 1024px) 100vw, 900px"
              />
            </div>
          </figure>
        </Reveal>
        <Reveal delay={160}>
          <p className="mt-6 text-center">
            <Link
              href="/product#screenshots"
              className="inline-flex items-center gap-1.5 text-[13.5px] font-medium text-brand-300 hover:text-brand-200 transition"
            >
              {t("landing.appShot.cta")}
              <ArrowRight size={14} />
            </Link>
          </p>
        </Reveal>
      </div>
    </section>
  );
}

function Example() {
  const t = useT();
  return (
    <section id="example" className="border-b border-line-subtle">
      <div className="mx-auto max-w-3xl px-5 py-20 sm:px-6 md:py-28">
        <Reveal>
          <div className="mb-5 text-[11px] font-medium uppercase tracking-[0.16em] text-fg-muted">
            {t("landing.example.label")}
          </div>
          <SectionHeader title={t("landing.example.title")} />
        </Reveal>
        <ol className="mt-12 space-y-0">
          {MOMENTS.map((key, i) => (
            <Reveal
              as="li"
              key={key}
              delay={i * 40}
              className="relative flex gap-5 pb-10 last:pb-0"
            >
                {i < MOMENTS.length - 1 && (
                  <span
                    aria-hidden
                    className="absolute left-[15px] top-9 bottom-0 w-px bg-line-subtle"
                  />
                )}
                <span className="z-10 flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-brand-500/30 bg-bg-raised text-[12.5px] font-semibold text-brand-300">
                  {i + 1}
                </span>
                <div className="min-w-0 pt-1">
                  <div className="text-[11px] font-mono uppercase tracking-[0.16em] text-fg-muted">
                    {t(`landing.example.moments.${key}.tag`)}
                  </div>
                  <div className="mt-1 text-[15.5px] font-semibold text-fg-bright">
                    {t(`landing.example.moments.${key}.title`)}
                  </div>
                  <p className="mt-1.5 text-[14px] leading-relaxed text-fg-dim">
                    {t(`landing.example.moments.${key}.body`)}
                  </p>
                </div>
            </Reveal>
          ))}
        </ol>
        <Reveal delay={120}>
          <p className="mt-10 border-t border-line-subtle pt-8 text-[15px] font-medium text-fg-bright">
            {t("landing.example.close")}
          </p>
        </Reveal>
        {/* A second, shorter example that starts in engineering — so the
            "start from engineering" path feels as real as the support one,
            without repeating the eight-step chain. */}
        <Reveal delay={80}>
          <div className="card mt-12 p-6 md:p-7">
            <div className="text-[11px] font-medium uppercase tracking-[0.16em] text-fg-muted">
              {t("landing.example.engineering.label")}
            </div>
            <h3 className="mt-2 text-[17px] font-semibold leading-snug text-fg-bright">
              {t("landing.example.engineering.title")}
            </h3>
            <ol className="mt-4 space-y-2">
              {ENGINEERING_EXAMPLE_STEPS.map((key, i) => (
                <li key={key} className="flex gap-3 text-[14px] leading-relaxed text-fg-dim">
                  <span
                    aria-hidden
                    className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full border border-brand-500/30 bg-brand-500/10 text-[11px] font-semibold text-brand-300"
                  >
                    {i + 1}
                  </span>
                  {t(`landing.example.engineering.steps.${key}`)}
                </li>
              ))}
            </ol>
          </div>
        </Reveal>
      </div>
      {/* Logo strip: recognition directly under the example — deliberately
          mid-page, above the fold it would categorize us as iPaaS. */}
      <div id="integrations" className="border-t border-line-subtle bg-bg-raised/40">
        <div className="mx-auto max-w-6xl px-5 py-10 text-center sm:px-6">
          <Reveal>
            <div className="text-[11px] font-medium uppercase tracking-[0.16em] text-fg-muted">
              {t("landing.integrations.label")}
            </div>
            {/* The answer to every organisation's first objection — given
                heading weight, not a trailing clause after the names. */}
            <p className="mx-auto mt-3 max-w-3xl text-balance text-xl font-semibold leading-snug tracking-tight text-fg-bright md:text-2xl">
              {t("landing.integrations.title")}
            </p>
            <p className="mt-3 text-[14px] text-fg-dim">{t("landing.integrations.names")}</p>
            <Link
              href="/integrations"
              className="mt-3 inline-flex items-center gap-1 text-[13px] text-brand-300 hover:text-brand-200 transition"
            >
              {t("landing.integrations.linkLabel")}
              <ArrowRight size={13} />
            </Link>
          </Reveal>
        </div>
      </div>
    </section>
  );
}

/* -------------------------------------------------------------------------- */
/*  VIDEO — the one-minute product video as proof under the positioning,   */
/*      plus the two demo cards toward the use-case pages. The video never     */
/*      autoplays (poster + explicit play → accessible modal); the demo cards  */
/*      show a quiet preview only when the short deliverables are published    */
/*      in the video manifest AND the autoplay rules allow it.                 */
/* -------------------------------------------------------------------------- */

const VIDEO_POINTS = ["p1", "p2", "p3", "p4"] as const;

function VideoSection() {
  const t = useT();
  const video = getMarketingVideo("fixcontrol-overview-nl");
  const [videoOpen, setVideoOpen] = useState(false);
  return (
    <section id="video" className="relative overflow-hidden border-b border-line-subtle bg-bg-raised/25">
      <div className="mx-auto max-w-6xl px-5 py-20 sm:px-6 md:py-28">
        <Reveal>
          <div className="text-center">
            <span className="text-[11px] font-mono uppercase tracking-[0.18em] text-brand-300">
              {t("landing.video.eyebrow")}
            </span>
          </div>
          <div className="mt-4">
            <SectionHeader
              align="center"
              title={t("landing.video.title")}
              description={t("landing.video.intro")}
            />
          </div>
        </Reveal>
        {/* The product video's poster with an explicit play button. Clicking
            opens the accessible modal player — never autoplay, no media bytes
            before intent. It used to be the hero visual; the eight-step chain
            took that place, so the video is now supporting proof. */}
        <Reveal delay={100}>
          <div className="relative mx-auto mt-10 max-w-4xl">
            <div className="pointer-events-none absolute -inset-12 -z-10 bg-[radial-gradient(ellipse_at_center,rgba(244,63,142,0.14),transparent_70%)]" />
            <VideoPoster
              video={video}
              onOpen={() => setVideoOpen(true)}
              ariaLabel={t("videoKit.playAriaLabel", {
                title: video.title,
                duration: `${Math.round(video.durationSeconds)}s`,
              })}
              caption={t("landing.video.posterCaption")}
            />
          </div>
        </Reveal>
        {/* Outside <Reveal>: its transform makes the wrapper the containing
            block for `position: fixed`, which boxed the full-screen modal
            into the poster's rectangle. */}
        <VideoModal
          video={video}
          title={video.title}
          open={videoOpen}
          onClose={() => setVideoOpen(false)}
        />
        <Reveal delay={140}>
          <ul className="mx-auto mt-10 grid max-w-4xl grid-cols-1 gap-x-8 gap-y-2.5 sm:grid-cols-2">
            {VIDEO_POINTS.map((key) => (
              <li key={key} className="flex items-start gap-2 text-[13.5px] text-fg-dim">
                <CheckCircle2 size={14} className="mt-0.5 shrink-0 text-brand-300" />
                {t(`landing.video.points.${key}`)}
              </li>
            ))}
          </ul>
        </Reveal>
        <Reveal delay={180}>
          <VideoTranscript video={video} />
        </Reveal>

        <Reveal delay={100}>
          <div className="mt-20">
            <SectionHeader
              align="center"
              title={t("landing.demos.title")}
              description={t("landing.demos.intro")}
            />
          </div>
        </Reveal>
        <div className="mx-auto mt-10 grid max-w-4xl gap-4 sm:grid-cols-2">
          <Reveal>
            <VideoCard
              href="/use-cases/support"
              title={t("landing.demos.support.title")}
              description={t("landing.demos.support.body")}
              cta={t("landing.demos.support.cta")}
              icon={<Mail size={18} />}
              previewId="fixcontrol-support-short-nl"
            />
          </Reveal>
          <Reveal delay={60}>
            <VideoCard
              href="/use-cases/engineering"
              title={t("landing.demos.engineering.title")}
              description={t("landing.demos.engineering.body")}
              cta={t("landing.demos.engineering.cta")}
              icon={<GitBranch size={18} />}
              previewId="fixcontrol-engineering-short-nl"
            />
          </Reveal>
        </div>
      </div>
    </section>
  );
}

/* -------------------------------------------------------------------------- */
/*  USE CASES — driven by the registry's availability, never a hardcoded    */
/*     tile for something that does not run (V4.6 Gate 2).                     */
/* -------------------------------------------------------------------------- */

/** Homepage tile order. Each id must resolve in the use-case registry; the
 *  tile renders only when the manifest exists and is not hidden, and carries
 *  a label for beta/preview availability. `custom` is deliberately not a
 *  marketing tile. */
const HOMEPAGE_USE_CASES = ["support", "engineering", "deployment", "security", "knowledge"] as const;

function UseCasesSection() {
  const t = useT();
  const tiles = HOMEPAGE_USE_CASES.map((id) => ({ id, def: resolveUseCase(id) })).filter(
    (x): x is { id: (typeof HOMEPAGE_USE_CASES)[number]; def: NonNullable<ReturnType<typeof resolveUseCase>> } =>
      Boolean(x.def) && x.def!.availability !== "hidden",
  );
  return (
    <section id="use-cases" className="border-b border-line-subtle">
      <div className="mx-auto max-w-6xl px-5 py-20 sm:px-6 md:py-28">
        <Reveal>
          <SectionHeader
            title={t("landing.useCases.title")}
            description={t("landing.useCases.intro")}
          />
        </Reveal>
        <div className="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {tiles.map(({ id }, i) => (
            <Reveal key={id} delay={i * 60}>
              {/* No availability chip: the body copy itself states honestly
                  what each use case does (and doesn't) do today. `hidden`
                  entries are still filtered out above. */}
              <div className="card h-full p-6">
                <div className="text-[15px] font-semibold text-fg-bright">
                  {t(`landing.useCases.items.${id}.title`)}
                </div>
                <p className="mt-2.5 text-[13.5px] leading-relaxed text-fg-dim">
                  {t(`landing.useCases.items.${id}.body`)}
                </p>
              </div>
            </Reveal>
          ))}
        </div>
        <Reveal delay={140}>
          <Link
            href="/use-cases"
            className="mt-8 inline-flex items-center gap-1 text-[13.5px] text-brand-300 hover:text-brand-200 transition"
          >
            {t("landing.useCases.linkLabel")}
            <ArrowRight size={13} />
          </Link>
        </Reveal>
      </div>
    </section>
  );
}

/* -------------------------------------------------------------------------- */
/*  CONTROL — rules + the customer capability ladder                        */
/* -------------------------------------------------------------------------- */

const RULES = ["r1", "r2", "r3", "r4"] as const;

/** Customer-language ladder, mapped 1:1 to the capability catalogue kinds
 *  (src/lib/use-cases/capabilities.ts): observe → analyze → prepare →
 *  external_action. The internal names stay invisible on the page; this
 *  mapping is the contract that keeps site and product the same ladder. */
const LADDER: ReadonlyArray<{ kind: ProjectCapabilityKind; key: string }> = [
  { kind: "observe", key: "observe" },
  { kind: "analyze", key: "analyze" },
  { kind: "prepare", key: "prepare" },
  { kind: "external_action", key: "external" },
];

function Control() {
  const t = useT();
  return (
    <section id="governance" className="border-b border-line-subtle">
      <div className="mx-auto max-w-6xl px-5 py-20 sm:px-6 md:py-28">
        <Reveal>
          <SectionHeader title={t("landing.control.title")} description={t("landing.control.intro")} />
        </Reveal>
        <div className="mt-10 grid gap-3 md:grid-cols-2">
          {RULES.map((key, i) => (
            <Reveal key={key} delay={i * 50}>
              <div className="card flex items-start gap-3 p-5">
                <ShieldCheck size={16} className="mt-0.5 shrink-0 text-brand-300" />
                <p className="text-[14px] leading-relaxed text-fg-dim">
                  {t(`landing.control.rules.${key}`)}
                </p>
              </div>
            </Reveal>
          ))}
        </div>
        <Reveal delay={120}>
          <p className="mt-8 max-w-3xl text-[15px] font-medium text-fg-bright">
            {t("landing.control.outro")}
          </p>
        </Reveal>

        <div className="mt-16">
          <Reveal>
            <h3 className="text-xl font-semibold tracking-tight text-fg-bright">
              {t("landing.control.ladder.title")}
            </h3>
          </Reveal>
          <div className="mt-6 grid gap-4 md:grid-cols-4">
            {LADDER.map(({ key }, i) => (
              <Reveal key={key} delay={i * 60}>
                <div className="card h-full p-5">
                  <div className="flex items-center gap-2.5">
                    <span className="flex h-6 w-6 items-center justify-center rounded-full border border-brand-500/30 bg-brand-500/10 text-[12px] font-semibold text-brand-300">
                      {i + 1}
                    </span>
                    <div className="text-[14px] font-semibold text-fg-bright">
                      {t(`landing.control.ladder.steps.${key}.title`)}
                    </div>
                  </div>
                  <p className="mt-2.5 text-[13px] leading-relaxed text-fg-dim">
                    {t(`landing.control.ladder.steps.${key}.body`)}
                  </p>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal delay={140}>
            <p className="mt-6 max-w-3xl text-[14px] text-fg-dim">
              {t("landing.control.ladder.outro")}
            </p>
          </Reveal>
        </div>
      </div>
    </section>
  );
}

/* -------------------------------------------------------------------------- */
/*  BOUNDARIES — replaces nothing; architecture comparison, no absolutes    */
/* -------------------------------------------------------------------------- */

const BOUNDARY_ITEMS = [
  { key: "workflow", href: "/compare/workflow-automation" },
  { key: "search", href: "/compare/enterprise-search" },
  { key: "platform", href: "/compare/servicenow" },
] as const;

function Boundaries() {
  const t = useT();
  return (
    <section id="compare" className="border-b border-line-subtle">
      <div className="mx-auto max-w-6xl px-5 py-20 sm:px-6 md:py-28">
        <Reveal>
          <SectionHeader title={t("landing.boundaries.title")} />
        </Reveal>
        <div className="mt-10 grid gap-4 md:grid-cols-3">
          {BOUNDARY_ITEMS.map(({ key, href }, i) => (
            <Reveal key={key} delay={i * 70}>
              <div className="card flex h-full flex-col p-6">
                <div className="text-[15px] font-semibold text-fg-bright">
                  {t(`landing.boundaries.items.${key}.title`)}
                </div>
                <p className="mt-2.5 flex-1 text-[13.5px] leading-relaxed text-fg-dim">
                  {t(`landing.boundaries.items.${key}.body`)}
                </p>
                <Link
                  href={href}
                  className="mt-4 inline-flex items-center gap-1 text-[13px] text-brand-300 hover:text-brand-200 transition"
                >
                  {t(`landing.boundaries.items.${key}.link`)}
                  <ArrowRight size={13} />
                </Link>
              </div>
            </Reveal>
          ))}
        </div>
        <Reveal delay={160}>
          <p className="mt-8 max-w-3xl text-[15px] font-medium text-fg-bright">
            {t("landing.boundaries.outro")}
          </p>
        </Reveal>
      </div>
    </section>
  );
}

/* -------------------------------------------------------------------------- */
/*  TRUST — verify us before you trust us                                   */
/* -------------------------------------------------------------------------- */

const TRUST_CHECKS = ["c1", "c2", "c3", "c4"] as const;
const TRUST_NOT_YET = ["n1", "n2"] as const;
const TRUST_FAQ = ["1", "2", "3"] as const;

function Trust() {
  const t = useT();
  return (
    <section id="trust" className="border-b border-line-subtle">
      <div className="mx-auto max-w-6xl px-5 py-20 sm:px-6 md:py-28">
        <Reveal>
          <SectionHeader title={t("landing.trust.title")} />
        </Reveal>
        <div className="mt-10 grid gap-8 md:grid-cols-2">
          <div>
            <Reveal>
              <h3 className="text-[15px] font-semibold text-fg-bright">
                {t("landing.trust.checkTitle")}
              </h3>
            </Reveal>
            <ul className="mt-4 space-y-3">
              {TRUST_CHECKS.map((key, i) => (
                <Reveal as="li" key={key} delay={i * 50} className="flex gap-2.5">
                  <CheckCircle2 size={15} className="mt-1 shrink-0 text-status-ok" />
                  <span className="text-[13.5px] leading-relaxed text-fg-dim">
                    {t(`landing.trust.checks.${key}`)}
                  </span>
                </Reveal>
              ))}
            </ul>
            <Reveal delay={200}>
              <div className="mt-5 flex flex-wrap gap-4 text-[13px]">
                <Link href="/security" className="text-brand-300 hover:text-brand-200 transition">
                  /security
                </Link>
                <Link href="/product" className="text-brand-300 hover:text-brand-200 transition">
                  /product
                </Link>
                <Link href="/pricing" className="text-brand-300 hover:text-brand-200 transition">
                  /pricing
                </Link>
              </div>
            </Reveal>
          </div>
          <div>
            <Reveal delay={60}>
              <h3 className="text-[15px] font-semibold text-fg-bright">
                {t("landing.trust.notYetTitle")}
              </h3>
            </Reveal>
            <ul className="mt-4 space-y-3">
              {TRUST_NOT_YET.map((key, i) => (
                <Reveal as="li" key={key} delay={60 + i * 50} className="flex gap-2.5">
                  <Eye size={15} className="mt-1 shrink-0 text-fg-muted" />
                  <span className="text-[13.5px] leading-relaxed text-fg-dim">
                    {t(`landing.trust.notYet.${key}`)}
                  </span>
                </Reveal>
              ))}
            </ul>
          </div>
        </div>
        <Reveal delay={100}>
          <h3 className="mt-12 text-[15px] font-semibold text-fg-bright">
            {t("landing.trust.faqTitle")}
          </h3>
        </Reveal>
        <div className="mt-4 grid gap-4 md:grid-cols-3">
          {TRUST_FAQ.map((n, i) => (
            <Reveal
              key={n}
              delay={100 + i * 50}
              className="rounded-2xl border border-line-subtle bg-bg-raised p-5"
            >
              <div className="text-[13.5px] font-semibold text-fg-bright">
                {t(`landing.trust.faq.q${n}`)}
              </div>
              <p className="mt-2 text-[13px] leading-relaxed text-fg-dim">
                {t(`landing.trust.faq.a${n}`)}
              </p>
            </Reveal>
          ))}
        </div>
        <Reveal delay={120}>
          <div className="mt-10 rounded-2xl border border-line-subtle bg-bg-raised p-6 md:p-8">
            <h3 className="text-[15px] font-semibold text-fg-bright">
              {t("landing.trust.partner.title")}
            </h3>
            <p className="mt-2 max-w-3xl text-[13.5px] leading-relaxed text-fg-dim">
              {t("landing.trust.partner.body")}
            </p>
            <Link
              href="/demo"
              className="mt-4 inline-flex items-center gap-1.5 text-[13.5px] font-medium text-brand-300 hover:text-brand-200 transition"
            >
              {t("landing.trust.partner.cta")}
              <ArrowRight size={14} />
            </Link>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

/* -------------------------------------------------------------------------- */
/*  FINAL CTA + FOOTER                                                     */
/* -------------------------------------------------------------------------- */

function FinalCTA() {
  const t = useT();
  return (
    <section className="relative overflow-hidden border-b border-line-subtle">
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_at_center,rgba(244,63,142,0.16),transparent_65%)]"
      />
      <div className="relative mx-auto max-w-3xl px-5 py-28 text-center sm:px-6 md:py-36">
        <Reveal>
          <h2 className="text-balance text-4xl font-semibold tracking-tight text-fg-bright md:text-6xl">
            {t("landing.finalCta.title")}
          </h2>
        </Reveal>
        <Reveal delay={100}>
          <p className="mt-5 text-balance text-[15px] text-fg-dim md:text-lg">
            {t("landing.finalCta.body")}
          </p>
        </Reveal>
        <Reveal delay={180}>
          <div className="mt-9 flex flex-wrap items-center justify-center gap-3">
            <PrimaryCTA href="/signup">
              {t("landing.finalCta.ctaPrimary")}
              <ArrowRight size={15} />
            </PrimaryCTA>
            <Link
              href="/demo"
              className="inline-flex items-center gap-1.5 rounded-lg border border-line bg-bg-raised px-4 py-2.5 text-[14px] text-fg hover:bg-bg-hover hover:border-line-strong transition"
            >
              {t("landing.finalCta.ctaSecondary")}
            </Link>
          </div>
        </Reveal>
        <Reveal delay={240}>
          <p className="mx-auto mt-6 max-w-xl text-pretty text-[13.5px] leading-relaxed text-fg-muted">
            {t("landing.finalCta.pilotNote")}
          </p>
        </Reveal>
      </div>
    </section>
  );
}

function Footer() {
  const t = useT();
  const productLinks = [
    { label: t("landing.footer.links.product"), href: "/product" },
    { label: t("landing.footer.links.howItWorks"), href: "#how" },
    { label: t("landing.footer.links.example"), href: "#example" },
    { label: t("landing.footer.links.integrations"), href: "#integrations" },
    { label: t("landing.footer.links.safety"), href: "#governance" },
    { label: t("landing.footer.links.review"), href: "#use-cases" },
  ];
  const resourceLinks = [
    { label: t("landing.footer.links.pricing"), href: "/pricing" },
    { label: t("landing.footer.links.docs"), href: "/docs/en" },
    { label: t("landing.footer.links.security"), href: "/security" },
    { label: t("landing.footer.links.api"), href: "/docs/en/admin/api" },
  ];
  const appLinks = [
    { label: t("landing.footer.links.login"), href: "/login" },
    { label: t("landing.footer.links.tryFree"), href: "/signup" },
    // "Try with your own issue" → signup with the issue-pilot intent (real
    // workspace), NOT the public intake widget that just dropped a ticket into
    // FixControl's own tenant with no environment for the visitor to see.
    { label: t("landing.footer.links.intake"), href: "/signup?intent=issues" },
  ];
  const contactLinks = [
    { label: t("landing.footer.links.demo"), href: "/demo" },
    { label: t("landing.footer.links.contactUs"), href: "/contact" },
    { label: t("landing.footer.links.privacy"), href: "/privacy" },
    { label: t("landing.footer.links.terms"), href: "/terms" },
    { label: PUBLIC_EMAIL, href: `mailto:${PUBLIC_EMAIL}` },
  ];
  return (
    <footer className="bg-bg-base">
      <div className="mx-auto max-w-6xl px-5 sm:px-6 py-14">
        <div className="grid grid-cols-2 md:grid-cols-5 gap-8">
          <div className="col-span-2 md:col-span-1">
            <BrandLogo size={20} wordmarkClassName="text-[15px]" />
            <p className="mt-4 text-[12px] text-fg-muted leading-relaxed">
              {t("landing.footer.tagline")}
            </p>
          </div>
          <FooterColumn title={t("landing.footer.product")} links={productLinks} />
          <FooterColumn title={t("landing.footer.resources")} links={resourceLinks} />
          <FooterColumn title={t("landing.footer.app")} links={appLinks} />
          <FooterColumn title={t("landing.footer.contact")} links={contactLinks} />
        </div>
        <div className="mt-10 pt-6 border-t border-line-subtle text-[12px] text-fg-muted">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-2">
            <span>
              {t("landing.footer.copyright", { year: new Date().getFullYear() })}
            </span>
            <span>{t("landing.footer.built")}</span>
          </div>
          <p className="mt-3 text-[11px] text-fg-muted">
            {COMPANY.chamberOfCommerceNumber && COMPANY.vatId
              ? t("landing.footer.legal", {
                  legal: COMPANY.legalName,
                  kvk: COMPANY.chamberOfCommerceNumber,
                  vat: COMPANY.vatId,
                })
              : t("landing.footer.legalPreRegistration", { legal: COMPANY.legalName })}
          </p>
        </div>
      </div>
    </footer>
  );
}

function FooterColumn({
  title,
  links,
}: {
  title: string;
  links: { label: string; href: string }[];
}) {
  return (
    <div>
      <div className="text-[11px] font-mono uppercase tracking-[0.18em] text-fg-muted">
        {title}
      </div>
      <ul className="mt-3 space-y-2 text-[13px] text-fg-dim">
        {links.map((l) => (
          <li key={l.label}>
            <a href={l.href} className="hover:text-fg-bright transition">
              {l.label}
            </a>
          </li>
        ))}
      </ul>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/*  PRIMITIVES                                                                 */
/* -------------------------------------------------------------------------- */

function PrimaryCTA({
  href,
  children,
}: {
  href: string;
  children: React.ReactNode;
}) {
  return (
    <Link
      href={href}
      className="inline-flex items-center gap-1.5 rounded-lg bg-brand-grad px-5 py-3 text-[14px] font-semibold text-white shadow-glow hover:brightness-110 transition"
    >
      {children}
    </Link>
  );
}

function SectionHeader({
  title,
  description,
  align = "left",
}: {
  title: React.ReactNode;
  description?: string;
  align?: "left" | "center";
}) {
  // Titles get the full content width so they run wide instead of wrapping
  // into a narrow column; the description stays at reading width.
  return (
    <div className={align === "center" ? "text-center" : ""}>
      <h2
        className={`max-w-5xl text-balance text-3xl md:text-5xl font-semibold tracking-tight text-fg-bright leading-[1.1] ${align === "center" ? "mx-auto" : ""}`}
      >
        {title}
      </h2>
      {description && (
        <p
          className={`mt-5 max-w-3xl text-pretty text-[15px] md:text-base leading-relaxed text-fg-dim ${align === "center" ? "mx-auto" : ""}`}
        >
          {description}
        </p>
      )}
    </div>
  );
}
